import React from 'react';
import { HelpCircle, ChevronRight, ShoppingBag, Store } from 'lucide-react';
import { useCartAssistant, useCartAssistantInput } from "../../../lib/cartAssistant/CartAssistantProvider";
import { useTranslation } from 'react-i18next';

interface DisambiguationCardProps {
  question: string;
  options: string[];
  type: 'item' | 'store';
}

export const DisambiguationCard: React.FC<{ props: DisambiguationCardProps; isStreaming?: boolean }> = ({ 
  props,
  isStreaming
}) => {
  const { t } = useTranslation();
  const { 
    question = t('chat.incomplete_data'), 
    options = [], 
    type = 'item' 
  } = props || {};
  
  const assistantInput = useCartAssistantInput();
  const { thread, cancelRun, isIdle } = useCartAssistant();
  const [pendingText, setPendingText] = React.useState<string | null>(null);

  // Always-current ref so async retries get the latest submit/setValue, not a stale closure
  const assistantInputRef = React.useRef(assistantInput);
  React.useLayoutEffect(() => {
    assistantInputRef.current = assistantInput;
  });

  // RESILIENCE: Check if thread is stuck waiting for tool calls
  const isStuck = React.useMemo(() => {
    const threadAny = thread as any;
    return threadAny?.thread?.status === 'requires_action' || 
           (threadAny?.pendingToolCallIds && threadAny.pendingToolCallIds.length > 0) ||
           (!isIdle && !threadAny?.streaming?.isActive);
  }, [thread, isIdle]);

  React.useEffect(() => {
    const fireSubmit = async (isRetry = false) => {
      if (!pendingText) return;
      
      const currentInput = assistantInputRef.current;
      
      if (currentInput.value === pendingText && !currentInput.isDisabled) {
        try {
          // PROACTIVE RECOVERY: If the thread is stuck waiting for tool calls from a previous run,
          // submit() will throw 400. We cancel the existing run here to ensure success.
          if (isStuck && !isRetry) {
            console.warn("[CartAssistant] [DisambiguationCard] Thread state is stuck. Attempting to cancel run...");
            if (cancelRun) {
              try {
                await cancelRun();
                console.log("[CartAssistant] [DisambiguationCard] Stuck run cancelled proactively.");
                await new Promise(resolve => setTimeout(resolve, 1500));
                currentInput.setValue(pendingText);
                await new Promise(resolve => setTimeout(resolve, 500));
              } catch (err) {
                console.error("[CartAssistant] [DisambiguationCard] Proactive cancel failed:", err);
              }
            }
          }

          console.log(`[CartAssistant] [DisambiguationCard] API SUBMIT INITIATED (isRetry: ${isRetry})`);
          await currentInput.submit();
          setPendingText(null);
        } catch (err: any) {
          const errorMsg = err?.message || String(err);
          const is400 = errorMsg.includes('400') || 
                       errorMsg.includes('MISSING_RESULTS') || 
                       err?.status === 400 ||
                       err?.code === 'MISSING_RESULTS';
          
          console.error(`[CartAssistant] [DisambiguationCard] Submission failed (isRetry: ${isRetry}):`, errorMsg);
          
          const isInvalidPreviousRun = errorMsg.includes('invalid_previous_run') ||
                                       errorMsg.includes('Invalid Previous Run');

          if (is400 && !isRetry) {
             if (isInvalidPreviousRun) {
               console.warn("[CartAssistant] [DisambiguationCard] invalid_previous_run detected. Retrying...");
               try {
                 await new Promise(resolve => setTimeout(resolve, 2500));
                 assistantInputRef.current.setValue(pendingText);
                 if (!assistantInputRef.current.isDisabled) {
                   await assistantInputRef.current.submit();
                   setPendingText(null);
                 }
               } catch (recoveryErr) {
                 console.error("[CartAssistant] [DisambiguationCard] invalid_previous_run retry failed:", recoveryErr);
                 setPendingText(null);
               }
             } else if (!isIdle) {
               console.warn("[CartAssistant] [DisambiguationCard] 400 MISSING_RESULTS detected. Attempting auto-recovery retry...");
               try {
                 if (cancelRun) {
                   await cancelRun();
                   await new Promise(resolve => setTimeout(resolve, 1500));
                   assistantInputRef.current.setValue(pendingText);
                   await new Promise(resolve => setTimeout(resolve, 500));
                   return await fireSubmit(true);
                 }
               } catch (recoveryErr) {
                 console.error("[CartAssistant] [DisambiguationCard] Recovery retry failed:", recoveryErr);
               }
             } else {
               console.warn("[CartAssistant] [DisambiguationCard] SDK state desync detected. Skipping futile retry.");
               setPendingText(null);
             }
          } else {
            setPendingText(null);
          }
        }
      }
    };

    fireSubmit();
  }, [pendingText, assistantInput.value, assistantInput.isDisabled, cancelRun, isStuck, isIdle]);

  const handleSelect = (option: string) => {
    if (!assistantInput.isDisabled) {
      assistantInput.setValue(option);
      setPendingText(option);
    }
  };

  if (options.length === 0 && !isStreaming) {
    return null;
  }

  return (
    <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="p-6 bg-indigo-500 text-white flex items-center gap-3">
        <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
          <HelpCircle size={18} />
        </div>
        <div>
          <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-100 leading-none mb-1">{t('chat.ai_clarification')}</h4>
          <p className="text-sm font-black text-white leading-tight">{question}</p>
        </div>
      </div>

      <div className="p-4 bg-white grid grid-cols-1 gap-2">
        {options.map((option, i) => (
          <button
            key={i}
            onClick={() => handleSelect(option)}
            className="w-full p-4 rounded-2xl bg-gray-50 border border-gray-100 hover:bg-indigo-50 hover:border-indigo-200 transition-all group text-left flex items-center justify-between"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-indigo-500 shadow-sm shrink-0">
                {type === 'item' ? <ShoppingBag size={14} /> : <Store size={14} />}
              </div>
              <span className="text-[13px] font-bold text-[#1a2b3c] truncate">{option}</span>
            </div>
            <ChevronRight size={16} className="text-gray-300 group-hover:text-indigo-500 transition-colors" />
          </button>
        ))}
      </div>
    </div>
  );
};
